import {
    ApolloClient, ApolloLink, concat, createHttpLink,
    HttpLink,
    InMemoryCache,
    ServerError,
} from "@apollo/client";
import { GraphQLErrors } from "@apollo/client/errors";

import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";

import { GraphQLError, GraphQLFormattedError } from "graphql";

import { LoggingHandler } from "./contexts/logging/LoggingContext";

export const FQOAL_GRAPHQL_URL_NEW = process.env.REACT_APP_API_URL_NEW;

const link = createHttpLink({
    uri: FQOAL_GRAPHQL_URL_NEW,
    credentials: "include",
}) as HttpLink;

const WHITELISTED_PATHS = ["/login", "/answers", "/channel-invite", "/onboarding/wait-for-domain", "/actions"];

const UNAUTHORIZED_MESSAGE = "Not authorized to perform this action.";

type ErroHandler = () => void;
const authErrorRegistry: ErroHandler[] = [];

export function subscribeError(err: ErroHandler) {
    authErrorRegistry.push(err);
}

export function unsubscribeError(err: ErroHandler) {
    const index = authErrorRegistry.findIndex(err);
    authErrorRegistry.splice(index, 1);
}

export function hasLoginError(errors?: readonly GraphQLFormattedError[]) {
    return errors?.some(error => {
        const ext = error.extensions as {code: number, exception: {code: string}} | undefined;
        return error.message === UNAUTHORIZED_MESSAGE || ext?.code === 401 || ext?.exception?.code === "401";
    });
}

function handleLoginError(operationName: string, errors?: readonly GraphQLFormattedError[]) {
    const currentPath = window.location.pathname;

    if (hasLoginError(errors)
        && (WHITELISTED_PATHS.every(path => !currentPath?.startsWith(path) && currentPath !== "/"))) {
        authErrorRegistry.forEach(handler => handler());

        LoggingHandler.instance.error({
            component: "Auth",
            operationName,
        }, "Not Authenticated. Redirecting to login");

        const params = new URLSearchParams();
        params.append("redirect", window.location.href);
        window.location.href = `/login?${params.toString()}`;
    }
}

const timeStartLink = new ApolloLink((operation, forward) => {
    operation.setContext({ startTime: performance.now() });
    return forward(operation);
});

const logLink = new ApolloLink((operation, forward) => forward(operation).map(response => {
    const context = operation.getContext();

    if (operation.operationName === "StoreLogs") {
        return response;
    }
    LoggingHandler.instance.debug({
        component: "API",
        requestId: context?.response?.headers?.get("x-req-id"),
        operationName: operation.operationName,
        responseStatus: context?.response.status,
        responseTime: performance.now() - context.startTime as number,
    }, "API request");

    return response;
}));

const errorLink = onError(err => {
    const { graphQLErrors, networkError, operation } = err;
    // const has401 = graphQLErrors?.find(err => err.extensions.)
    if (graphQLErrors) {
        const context = operation.getContext();
        // eslint-disable-next-line no-console
        LoggingHandler.instance.error({
            component: "API",
            requestId: context?.response?.headers?.get("x-req-id"),
            operationName: operation.operationName,
            responseStatus: context?.response.status,
            responseTime: performance.now() - context.startTime as number,
            errors: graphQLErrors.map(graphError => ({
                message: graphError.message,
                location: graphError.locations,
                path: graphError.path,
                innerError: graphError,
            })),
        }, "GraphQL Error");

        handleLoginError(operation.operationName, graphQLErrors);
    }

    if (networkError) {
        // eslint-disable-next-line no-console
        LoggingHandler.instance.error(networkError, { component: "API" }, "Network error occured");
    }
});

const retryLink = new RetryLink({
    delay: {
        initial: 500,
        max: 10000,
        jitter: true,
    },
    attempts: {
        max: 3,
        retryIf: (err, operation) => {
            const serverError = err.name === "ServerError" ? err as ServerError : undefined;

            LoggingHandler.instance.warn({
                component: "API",
                operationName: operation.operationName,
                responseStatus: serverError?.response.status,
                message: err.message,
                requestId: serverError?.response.headers.get("x-req-id"),
            }, "Retrying request");

            return ((err instanceof TypeError && err.message === "Failed to fetch")
                || (serverError != null && Math.floor(err.statusCode / 100) === 5));
        },
    },
});
const cache = new InMemoryCache();
const client = new ApolloClient({
    link: concat(timeStartLink, concat(retryLink, concat(errorLink, concat(logLink, link)))),
    cache,
});

export default client;

export function removeTypename<T extends {__typename?: string}>(obj: T): T {
    if (obj?.__typename) {
        // eslint-disable-next-line no-param-reassign
        delete obj.__typename;
    }
    Object.values(obj).forEach(value => {
        if (value) {
            if (Array.isArray(value)) {
                value.forEach(row => {
                    if (typeof row === "object") {
                        removeTypename(row);
                    }
                });
            } else if (typeof value === "object") {
                removeTypename(value);
            }
        }
    });
    return obj;
}

export function cloneWithoutTypename<T extends {__typename?: string} | {__typename?: string}[]>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj.map(row => cloneWithoutTypename(row)) as T;
    }

    if (obj == null || typeof obj !== "object" || obj instanceof Date) {
        return obj;
    }

    const copy: Record<keyof typeof obj, unknown> = Object.create(null);
    Object.entries(obj).forEach(([key, value]) => {
        if (key === "__typename") {
            return;
        }
        copy[key as keyof typeof copy] = cloneWithoutTypename(value as unknown as T);
    });
    return copy as T;
}

export function removeTypenameList<T extends {__typename?: string}>(obj: T[]): T[] {
    return obj.map(row => removeTypename({ ...row }));
}

export type DeepComparer = (path: string[], left: unknown, right: unknown) => boolean | undefined;

export function isDeepEqual(
    left: unknown,
    right: unknown,
    path?: string[],
    comparer?: DeepComparer,
): boolean {
    if (comparer) {
        const isEqual = comparer?.(path ?? [], left, right);
        if (isEqual != null) {
            return isEqual;
        }
    }

    if (left == null && right == null) {
        return true;
    }
    // eslint-disable-next-line eqeqeq
    if ((left == null || right == null) || (typeof left !== typeof right && left != right)) {
        return false;
    }

    if (Array.isArray(left) && Array.isArray(right)) {
        return left.length === right.length && left.every((element, index) =>
            // eslint-disable-next-line implicit-arrow-linebreak
            isDeepEqual(element, right[index], path ? [...path, index.toString()] : undefined, comparer));
    }

    if (typeof left === "object" && typeof right === "object") {
        const keysA = Object.keys(left).filter(key => key !== "__typename");
        const keysB = Object.keys(right).filter(key => key !== "__typename");

        return keysA.concat(keysB)
            .dedupe()
            .every(key => isDeepEqual(
                left[key as keyof typeof left],
                right[key as keyof typeof right],
                path ? [...path, key] : undefined,
                comparer,
            ));
    }

    // Otherwise, they are equal
    return left === right;
}

export function isHtmlEqual(left: string | undefined, right: string | undefined): boolean {
    const leftValue = left || "<p></p>";
    const rightValue = right || "<p></p>";
    // eslint-disable-next-line eqeqeq
    return leftValue == rightValue;
}
export function isEditorTextEqual(left: unknown, right: unknown): boolean {
    const leftValue = (left as {rawText: string | undefined})?.rawText || "<p></p>";
    const rightValue = (right as {rawText: string | undefined})?.rawText || "<p></p>";
    // eslint-disable-next-line eqeqeq
    return leftValue == rightValue;
}

export function createDeepEqual(comparer?: DeepComparer) {
    return (left: unknown, right: unknown) => isDeepEqual(left, right, undefined, comparer);
}
